import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { SessionManager } from './SessionManager';
import ConfirmationDialog from './components/ConfirmationDialog';
import { getBackendConfig } from './config/backend';

const ApiTest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [healthStatus, setHealthStatus] = useState<string>('');
  const [files, setFiles] = useState<string[]>([]);
  const [deleteStatus, setDeleteStatus] = useState<string>('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);


  // Get API URL from config
  const getApiUrl = () => {
    return getBackendConfig().apiBaseUrl;
  };

  const testHealthCheck = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${getApiUrl()}/`);
      const isHealthy = response.ok;
      setHealthStatus(isHealthy ? '✅ Backend is healthy and reachable' : '❌ Backend is not responding');
    } catch (err) {
      setError(`Health check failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setHealthStatus('❌ Health check failed');
    } finally {
      setLoading(false);
    }
  };

  const testGetFiles = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${getApiUrl()}/files`);
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('Error response:', errorText);
        throw new Error(`Failed to fetch files: ${response.status} ${response.statusText}`);
      }

      const responseText = await response.text();
      console.log('Response text:', responseText);

      let fileList;
      try {
        fileList = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 100)}...`);
      }

      if (Array.isArray(fileList)) {
        // If it's an array of objects, extract useful information including file ID
        const fileInfo = fileList.map((file, index) => {
          if (typeof file === 'string') {
            return file;
          } else if (typeof file === 'object' && file !== null) {
            // Try to extract filename and ID from object properties
            const fileName = file.filename || file.name || file.fileName || `File ${index + 1}`;
            const fileId = file.id || file.fileId || index + 1;
            return `${fileName} (${fileId})`;
          }
          return `File ${index + 1}`;
        });
        setFiles(fileInfo);
      } else {
        setFiles([]);
      }
    } catch (err) {
      setError(`Failed to fetch files: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteAllFiles = async () => {
    setLoading(true);
    setError('');
    setDeleteStatus('');

    try {
      const response = await fetch(`${getApiUrl()}/deleteAllFiles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete files: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      // Clear session data after deleting all files
      console.log('🗑️ Clearing session data after deleting all files');
      SessionManager.clearSession();
      SessionManager.initializeSession();

      setDeleteStatus('✅ All files deleted successfully and session cleared');
      setFiles([]); // Clear the files list since they're now deleted
      
      // Note: Files have been deleted from backend, but you may need to refresh the page manually
      // to see the updated file list in the main app
      console.log('ℹ️ Deletion completed. You may need to refresh the page to see updated file list.');
    } catch (err) {
      setError(`Failed to delete files: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setDeleteStatus('❌ Failed to delete files');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const confirmDeleteAllFiles = async () => {
    setShowDeleteDialog(false);
    await deleteAllFiles();
  };


  return (
    <Paper sx={{ p: 3, m: 2, maxWidth: 600 }}>
      <Typography variant="h5" gutterBottom>
        API Service Test
      </Typography>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Current API Base URL: <strong>{getApiUrl()}</strong>
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Environment: <strong>{process.env.NODE_ENV || 'development'}</strong>
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Data Storage: <strong>
          {getApiUrl().includes('localhost') ? 'PostgreSQL (Local)' : 'Google Cloud Run + Storage'}
        </strong>
      </Typography>

      <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Health Check */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Health Check
          </Typography>
          <Button
            variant="contained"
            onClick={testHealthCheck}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Test Backend Health
          </Button>
          {healthStatus && (
            <Alert severity={healthStatus.includes('✅') ? 'success' : 'error'} sx={{ mt: 1 }}>
              {healthStatus}
            </Alert>
          )}
        </Box>

        {/* Get Files */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Get Files
          </Typography>
          <Button
            variant="contained"
            onClick={testGetFiles}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Fetch Files from Backend
          </Button>
          {files.length > 0 ? (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Found {files.length} file{files.length !== 1 ? 's' : ''}:
              </Typography>
              <List dense sx={{ bgcolor: 'background.paper' }}>
                {files.map((file, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={file} />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            files.length === 0 && !loading && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No files found on the backend.
              </Typography>
            )
          )}
        </Box>

        {/* Delete All Files */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Reset App State
          </Typography>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteClick}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Delete All Files
          </Button>
          {deleteStatus && (
            <Alert severity={deleteStatus.includes('✅') ? 'success' : 'error'} sx={{ mt: 1 }}>
              {deleteStatus}
            </Alert>
          )}
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Confirmation Dialog */}
        <ConfirmationDialog
          open={showDeleteDialog}
          title="Delete All Files?"
          message="Are you sure you want to delete all files? This action cannot be undone."
          confirmText="Delete All"
          severity="error"
          onConfirm={confirmDeleteAllFiles}
          onCancel={() => setShowDeleteDialog(false)}
        />
      </Box>
    </Paper>
  );
};

export default ApiTest; 